import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useColors } from '../../theme/ColorModeContext';
import { copyToClipboard, facebookShareUrl, linkedInShareUrl, xShareUrl } from './share';

/** The X (formerly Twitter) logo. */
const XLogoIcon = (props: SvgIconProps) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </SvgIcon>
);

interface ShareResultProps {
    /** Link to share, or null when there is none (yet). */
    shareUrl: string | null;
    /** True while the link is still being created. */
    pending?: boolean;
    /** Post text for networks that accept one (X). */
    text: string;
    /** Optional line under the link, e.g. what the shared page reveals. */
    note?: string;
}

const ShareResult = ({ shareUrl, pending = false, text, note }: ShareResultProps) => {
    const colors = useColors();
    const [copied, setCopied] = useState(false);

    const copy = async () => {
        if (!shareUrl) return;
        if (await copyToClipboard(shareUrl)) {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        }
    };

    const networks = shareUrl
        ? [
              { label: 'X', href: xShareUrl(shareUrl, text), Icon: XLogoIcon },
              { label: 'Facebook', href: facebookShareUrl(shareUrl), Icon: FacebookIcon },
              { label: 'LinkedIn', href: linkedInShareUrl(shareUrl), Icon: LinkedInIcon },
          ]
        : [];

    return (
        <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${colors.border.subtle}` }}>
            <Typography
                sx={{
                    fontFamily: '"Share Tech Mono", monospace',
                    fontSize: '0.72rem',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: colors.text.secondary,
                    mb: 1.5,
                }}
            >
                Share your result
            </Typography>

            {shareUrl ? (
                <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px' }}>
                        {networks.map(({ label, href, Icon }) => (
                            <Button
                                key={label}
                                component="a"
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="outlined"
                                size="small"
                                startIcon={<Icon />}
                            >
                                {label}
                            </Button>
                        ))}
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={copy}
                            startIcon={copied ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />}
                        >
                            {copied ? 'Copied' : 'Copy link'}
                        </Button>
                    </Box>
                    <Typography
                        sx={{
                            fontFamily: '"Share Tech Mono", monospace',
                            fontSize: '0.75rem',
                            color: colors.text.muted,
                            mt: 1.5,
                            wordBreak: 'break-all',
                        }}
                    >
                        {shareUrl}
                    </Typography>
                    {note && (
                        <Typography sx={{ color: colors.text.muted, fontSize: '0.78rem', mt: 1 }}>{note}</Typography>
                    )}
                </>
            ) : (
                <Typography sx={{ color: colors.text.muted, fontSize: '0.82rem' }}>
                    {pending ? 'Creating your share link...' : 'Sharing is unavailable because this result could not be saved.'}
                </Typography>
            )}
        </Box>
    );
};

export default ShareResult;
